import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Calendar, Clock, CheckCircle, X, AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { acceptInvitation, declineInvitation } from '../services/invitationService';
import styles from './InvitationAccept.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function InvitationAccept() {
  const { token } = useParams();
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [acceptSuccess, setAcceptSuccess] = useState(false);
  const [declineSuccess, setDeclineSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mismatch, setMismatch] = useState(null); // { invitee_email, signed_in_email }

  useEffect(() => { loadInvitation(); }, [token]);

  // Anonymous prefetch — endpoint stays public so we can render the card
  // before the user authenticates. Only the POST accept is gated.
  const loadInvitation = async () => {
    try {
      setLoading(true); setError(null);
      const response = await fetch(`${API_URL}/v1/invitations/accept/${token}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Invitation not found or has expired.');
      }
      setInvitation(await response.json());
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setIsSubmitting(true); setError(null); setMismatch(null);
      await acceptInvitation(token);
      setAcceptSuccess(true);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      if (err.status === 403 && err.detail === 'auth_email_mismatch') {
        setMismatch({
          invitee_email: err.inviteeEmail,
          signed_in_email: err.signedInEmail,
        });
      } else if (err.status === 403 && err.detail === 'auth_method_invalid') {
        setError('This invitation must be accepted from a user account, not an API key.');
      } else if (err.status === 401) {
        setError('Your session has expired. Sign in again to accept.');
      } else {
        setError(err.message || 'Failed to accept invitation');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!confirm('Are you sure you want to decline this invitation?')) return;
    try {
      setIsSubmitting(true); setError(null);
      await declineInvitation(token);
      setDeclineSuccess(true);
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err.message || 'Failed to decline invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOutAndRestart = async () => {
    await signOut();
    setMismatch(null);
    // After sign-out the user falls through to the unauthed CTA branch.
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingCard}>
          <div className={styles.spinner} aria-label="Loading" />
          <p className={styles.loadingText}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconError}`}>
            <X size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>Invitation Not Available</h1>
          <p className={styles.stateBody}>{error}</p>
          <Link to="/auth" className={styles.stateLink}>Go to Vennio</Link>
        </div>
      </div>
    );
  }

  if (declineSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconNeutral}`}>
            <CheckCircle size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>Invitation Declined</h1>
          <p className={styles.stateBody}>
            You've declined the invitation from {invitation?.business?.name}.
          </p>
        </div>
      </div>
    );
  }

  if (acceptSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconSuccess}`}>
            <CheckCircle size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>You're Connected!</h1>
          <p className={styles.stateBody}>
            You're now connected with <strong>{invitation?.business?.name}</strong>.
          </p>

          <div className={styles.calendarPrompt}>
            <Calendar size={32} className={styles.calendarPromptIcon} aria-hidden="true" />
            <div>
              <h3 className={styles.calendarPromptTitle}>Get Better Meeting Times</h3>
              <p className={styles.calendarPromptBody}>
                Connect your calendar so {invitation?.business?.name} can find times that work for you.
                We only see your busy/free times, not event details.
              </p>
              <a href="/auth?redirect=/dashboard" className={styles.calendarPromptBtn}>
                <Calendar size={20} aria-hidden="true" />
                Connect Calendar
              </a>
            </div>
          </div>

          {invitation?.venn_link && (
            <div className={styles.vennLinkBox}>
              <h3 className={styles.vennLinkTitle}>Ready to Book?</h3>
              <p className={styles.vennLinkBody}>
                {invitation.business.name} has shared a booking link with you:
              </p>
              <a href={`/venn/${invitation.venn_link.short_code}`} className={styles.vennLinkBtn}>
                <Clock size={20} aria-hidden="true" />
                Book: {invitation.venn_link.title}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Email-mismatch error — the user is signed in but with a different email
  // than the one this invitation was sent to.
  if (mismatch) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <div className={`${styles.stateIcon} ${styles.stateIconError}`}>
            <AlertCircle size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.stateTitle}>Wrong Account</h1>
          <p className={styles.stateBody}>
            You're signed in as <strong>{mismatch.signed_in_email}</strong>, but this
            invitation was sent to <strong>{mismatch.invitee_email}</strong>.
          </p>
          <p className={styles.stateBody}>
            Sign out and sign in with the correct email to accept.
          </p>
          <button onClick={handleSignOutAndRestart} className={styles.stateLink}>
            Sign Out and Restart
          </button>
        </div>
      </div>
    );
  }

  // Branch only on auth presence, not email match. The backend is the
  // authoritative gate — let it return 403 on mismatch so handleAccept's
  // catch block can drive the mismatch screen. A client-side prediction
  // would loop the user back through AuthPage indefinitely (AuthPage's
  // existing useEffect auto-navigates authed users back to returnUrl).
  const inviteeEmail = invitation?.invitee_email || '';

  return (
    <div className={styles.page}>
      <div className={styles.inviteCard}>
        {/* Header */}
        <div className={styles.inviteHeader}>
          <div className={styles.inviteHeaderIcon}>
            <Mail size={32} aria-hidden="true" />
          </div>
          <h1 className={styles.inviteTitle}>You're Invited</h1>
          <p className={styles.inviteSubtitle}>
            <strong>{invitation?.business?.name}</strong> wants to connect with you on Vennio
          </p>
        </div>

        {/* Business info */}
        <div className={styles.businessBox}>
          <div className={styles.businessRow}>
            <div className={styles.businessAvatar}>
              {invitation?.business?.name?.charAt(0) || 'B'}
            </div>
            <div>
              <h2 className={styles.businessName}>{invitation?.business?.name}</h2>
              {invitation?.business?.service_type && (
                <p className={styles.businessType}>{invitation.business.service_type}</p>
              )}
            </div>
          </div>
          {invitation?.business?.description && (
            <p className={styles.businessDesc}>{invitation.business.description}</p>
          )}
        </div>

        {/* Message */}
        {invitation?.message && (
          <blockquote className={styles.messageQuote}>
            <p>"{invitation.message}"</p>
            <footer>— {invitation.business?.contact_name || invitation.business?.name}</footer>
          </blockquote>
        )}

        {/* Venn link preview */}
        {invitation?.venn_link && (
          <div className={styles.vennPreview}>
            <div className={styles.vennPreviewLabel}>
              <Clock size={16} className={styles.vennPreviewIcon} aria-hidden="true" />
              Includes booking link
            </div>
            <p className={styles.vennPreviewTitle}>{invitation.venn_link.title}</p>
            {invitation.venn_link.duration_minutes && (
              <p className={styles.vennPreviewDuration}>{invitation.venn_link.duration_minutes} minutes</p>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className={styles.errorBox}>
            <AlertCircle size={20} className={styles.errorIcon} aria-hidden="true" />
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Actions — branch on auth state */}
        {user ? (
          <div className={styles.actions}>
            <button onClick={handleAccept} disabled={isSubmitting} className={styles.acceptBtn}>
              {isSubmitting ? 'Accepting...' : 'Accept Invitation'}
            </button>
            <button onClick={handleDecline} disabled={isSubmitting} className={styles.declineBtn}>
              Decline
            </button>
          </div>
        ) : (
          <div className={styles.actions}>
            <a
              href={`/auth?redirect=${encodeURIComponent(`/invite/${token}`)}&prefill_email=${encodeURIComponent(inviteeEmail)}`}
              className={styles.acceptBtn}
            >
              <LogIn size={20} aria-hidden="true" />
              Sign in or create an account to accept
            </a>
            <p className={styles.privacyNote}>
              This invitation was sent to <strong>{inviteeEmail}</strong>.
              You'll need to sign in (or create a free Vennio account) with that
              email address to accept.
            </p>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <p>Powered by <span className={styles.footerBrand}>Vennio</span></p>
      </div>
    </div>
  );
}
